import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, getNetworkConfig } from "@/lib/api";

const getPairs = () => api.get("/dex/pairs").then((r) => r.data);
const getListed = () => api.get("/tokenlist.json").then((r) => r.data.tokens || []);

/** Pairs from the factory plus a token list: FMI, USDT, FBTC, listed tokens, every pair token and any imported by address. */
export function useDexData() {
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const pairsQ = useQuery({ queryKey: ["dexPairs"], queryFn: getPairs, refetchInterval: 15000 });
  const { data: listed } = useQuery({ queryKey: ["tokenList"], queryFn: getListed });
  const [imported, setImported] = useState([]);
  const wfmi = pairsQ.data?.wfmi;

  const tokens = useMemo(() => {
    const base = [];
    if (wfmi) base.push({ address: wfmi, symbol: "FMI", decimals: 18, isFmi: true, name: "FMI" });
    if (config?.usdt?.address) base.push({ address: config.usdt.address, symbol: "USDT", decimals: config.usdt.decimals, isFmi: false });
    if (config?.fbtc?.address) base.push({ address: config.fbtc.address, symbol: "FBTC", decimals: config.fbtc.decimals, isFmi: false });
    const listedTokens = (listed || []).map((t) => ({ address: t.address, symbol: t.symbol, decimals: t.decimals, name: t.name, isFmi: false }));
    const all = [...base, ...listedTokens, ...(pairsQ.data?.pairs || []).flatMap((p) => [p.token0, p.token1]), ...imported];
    const seen = new Set();
    return all.filter((t) => !seen.has(t.address.toLowerCase()) && seen.add(t.address.toLowerCase()));
  }, [wfmi, config, pairsQ.data, imported, listed]);

  const importToken = async (address) => {
    try {
      const { data } = await api.get(`/dex/token/${address.trim()}`);
      setImported((list) => [...list, data]);
      toast.success(`${data.symbol} added to the token list`);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load that token");
      return null;
    }
  };

  return { pairs: pairsQ.data?.pairs || [], router: pairsQ.data?.router, wfmi, tokens, importToken, refetchPairs: pairsQ.refetch, loading: pairsQ.isLoading };
}
